import { directSupabase } from '../../lib/supabaseClient';
import { documentsService } from '../../modules/docs/services/documents.service';
import { visualDesignerSurveysService } from '../../modules/docs/services/visualDesignerSurveys.service';
import { cloneLineForBoundVersion, isVisualDesignerLine } from '../../modules/docs/utils/visualDesignerSurveyClone';
import type { DocumentLineInput } from '../../modules/docs/types/documents.types';
import type { Project } from '../../types/project';
import { getFullWorkflowTasksForMode } from '../../utils/workflowUtils';
import {
  cloneCopyPlan,
  draftQuoteTitle,
  excludeVisualDesignerLines,
  pickCloneSourceDocument,
  primarySurvey,
  type CloneSourceKind,
  type CloneWorkspaceMode,
} from './workspaceQuote.helpers';
import { mintWorkspaceLifecycleRef } from './workspaceLifecycleRef';
import { brandForCustomer, getWorkspaceBrandId, resolveWorkspaceBrandId, saveWorkspaceBrand } from './workspaceBrand';
import { organisationSettings } from '../../services/organisationSettings';
import { defaultUseBillingDetailsForDocuments } from './workspaceBillingIdentity';
import { customerService } from '../../services/customerService';
import {
  emitProjectCreatedActivity,
  withProjectCreator,
} from '../../services/activityEvents.service';
import { COMMERCIAL_DELETE_BLOCKED_AFTER_FIRST_TRANSMIT } from '../../modules/docs/utils/surveySheetDelete';
import { copyWorkspaceComments } from './workspaceComments.service';
import { copyProjectSupplierReferences } from './supplierReferences.service';
import { listProjectJobCards } from './jobCard.service';
import { clonedSurveyNotes } from './jobCardSurveyNotes';
import { defaultDepositTerms, isWorkspaceSupplyOnly } from './workspaceSupplyOnly';

export type { CloneWorkspaceMode };

/** What a workspace quote delete is allowed to touch — keeps legacy quotes elsewhere intact. */
export function workspaceQuoteDeleteScope(input: {
  documentId: string;
  projectId: string;
}) {
  return {
    documentId: String(input.documentId || '').trim(),
    projectId: String(input.projectId || '').trim(),
    /** Only versions belonging to this document (legacy multi-version history for this quote alone). */
    versionsFilter: { document_id: String(input.documentId || '').trim() },
    /** Only surveys for this workspace. */
    surveysFilter: { project_id: String(input.projectId || '').trim() },
    /** Explicitly preserved. */
    preserve: [
      'other_projects_surveys',
      'other_documents_versions',
      'invoices_same_lifecycle_ref',
      'share_links_on_other_documents',
    ] as const,
  };
}

function splitName(name: string) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  return {
    first: parts[0] || null,
    last: parts.slice(1).join(' ') || null,
  };
}

function mapVersionLines(lines: any[]): DocumentLineInput[] {
  return (lines || []).map((line: any) => ({
    lineKind: line.line_kind || line.lineKind || 'other',
    additionalType: line.additional_type ?? line.additionalType ?? null,
    materialId: line.material_id || line.materialId || null,
    equipmentId: line.equipment_id || line.equipmentId || null,
    labourRateId: line.labour_rate_id || line.labourRateId || null,
    name: line.name,
    description: line.description || null,
    qty: Number(line.qty || 0),
    unit: line.unit || null,
    unitPrice: Number(line.unit_price ?? line.unitPrice ?? 0),
    vatInclusive: !!(line.vat_inclusive ?? line.vatInclusive),
    lineTotal: line.line_total != null ? Number(line.line_total) : (line.lineTotal != null ? Number(line.lineTotal) : undefined),
    sortOrder: line.sort_order ?? line.sortOrder ?? null,
    labourDays: line.labour_days ?? line.labourDays ?? null,
    labourHours: line.labour_hours ?? line.labourHours ?? null,
    metaJson: line.meta_json ?? line.metaJson ?? null,
  }));
}

async function attachFirstSurvey(
  projectId: string,
  versionId: string | null,
  copyFromProjectId?: string | null,
  documentId?: string | null,
) {
  // Prefer the orphan-aware binder so we never stay stuck on a deleted/transmitted version.
  const survey = versionId
    ? await visualDesignerSurveysService.ensureFirstSurveyBoundToQuote(projectId, documentId)
    : await visualDesignerSurveysService.ensureFirstSurvey(projectId);
  if (copyFromProjectId) {
    const sourceSurveys = await visualDesignerSurveysService.listByProject(copyFromProjectId);
    const source = primarySurvey(sourceSurveys, 'first');
    if (source) {
      const items = await visualDesignerSurveysService.listItems(source.id);
      await visualDesignerSurveysService.replaceItems(
        survey.id,
        items.filter(isVisualDesignerLine).map(cloneLineForBoundVersion),
      );
      if (versionId) {
        await visualDesignerSurveysService.projectToVersion(survey.id, versionId);
      }
    }
  } else if (versionId && survey.documentVersionId !== versionId) {
    await visualDesignerSurveysService.bindToVersion(survey.id, versionId);
  }
  return visualDesignerSurveysService.getById(survey.id);
}

export async function createDraftQuote(params: {
  projectId: string;
  customerId: string;
  customerName?: string | null;
  customerTitle?: string | null;
  projectName?: string | null;
  lifecycleRef?: string | null;
  copyFromProjectId?: string | null;
  kind?: CloneSourceKind;
}) {
  // Fresh blank quote on this workspace must not rehydrate a ghost First/Final Survey left
  // behind after an old quote was unlinked/deleted. Clone-into-new-workspace uses copyFrom instead.
  if (!params.copyFromProjectId) {
    await visualDesignerSurveysService.resetAllForProject(params.projectId);
  }

  // The quote is issued under the workspace's brand (default brand if it has none yet)
  const organisation = { id: await resolveWorkspaceBrandId(params.projectId) };
  const names = splitName(params.customerName || '');
  const created = await documentsService.createDocument({
    kind: params.kind || 'Quotation',
    lifecycleRef: params.lifecycleRef || '',
    organisationId: organisation.id,
    isOriginal: true,
    projectId: params.projectId,
    customerId: params.customerId,
    customerFirstName: names.first,
    customerLastName: names.last,
    customerTitle: params.customerTitle || null,
    title: draftQuoteTitle(params.projectName, params.customerName),
    description: '',
    notes: '',
    status: 'draft',
    currency: 'GBP',
    ...defaultDepositTerms(await isWorkspaceSupplyOnly(params.projectId)),
    source: 'jarvis',
    vatEnabled: true,
    vatPct: 0.2,
    inputVatSelected: false,
    fees: { adminPct: 0.10, adminCap: 200, contingencyPct: 0.10, profitPct: 0.10, developerPct: null },
  }, []);
  const active = await documentsService.getActiveVersion(created.id);
  const versionId = active?.header?.id ? String(active.header.id) : null;
  await attachFirstSurvey(params.projectId, versionId, params.copyFromProjectId, created.id);
  return created;
}

/**
 * Hard-delete a workspace Quotation/Estimate and reset that workspace's survey stack.
 * Scoped so legacy version histories on other quotes/projects are never touched.
 * Invoices (even same lifecycle ref) are preserved.
 */
export async function deleteWorkspaceCommercialDocument(params: {
  documentId: string;
  projectId: string;
}): Promise<void> {
  const scope = workspaceQuoteDeleteScope(params);
  if (!scope.documentId || !scope.projectId) {
    throw new Error('Missing document or project id');
  }

  const { data: doc, error: docErr } = await directSupabase
    .from('documents')
    .select('id, kind, project_id')
    .eq('id', scope.documentId)
    .maybeSingle();
  if (docErr) throw new Error(docErr.message);
  if (!doc) throw new Error('Document not found');
  if (doc.kind !== 'Quotation' && doc.kind !== 'Estimate') {
    throw new Error('Only quotes or estimates can be deleted from the workspace commercial lane.');
  }
  if (String(doc.project_id || '') !== scope.projectId) {
    throw new Error('This document is not linked to this workspace.');
  }

  if (await visualDesignerSurveysService.projectFirstSurveyIsImmutable(scope.projectId)) {
    throw new Error(COMMERCIAL_DELETE_BLOCKED_AFTER_FIRST_TRANSMIT);
  }

  // quote_options.working_version_id is ON DELETE RESTRICT — remove options before versions.
  const { error: optErr } = await directSupabase
    .from('quote_options')
    .delete()
    .eq('commercial_document_id', scope.documentId);
  if (optErr && !/does not exist|relation .* does not exist/i.test(String(optErr.message || ''))) {
    throw new Error(optErr.message);
  }

  const { error: clearActiveErr } = await directSupabase
    .from('documents')
    .update({ active_version_id: null })
    .eq('id', scope.documentId);
  if (clearActiveErr) throw new Error(clearActiveErr.message);

  // Legacy multi-version history for THIS document only.
  const { data: versionRows, error: verListErr } = await directSupabase
    .from('document_versions')
    .select('id')
    .eq('document_id', scope.documentId);
  if (verListErr) throw new Error(verListErr.message);
  const versionIds = (versionRows || []).map((row: { id: string }) => row.id);
  if (versionIds.length > 0) {
    const { error: verLinesErr } = await directSupabase
      .from('document_version_lines')
      .delete()
      .in('version_id', versionIds);
    if (verLinesErr) throw new Error(verLinesErr.message);
  }
  const { error: verErr } = await directSupabase
    .from('document_versions')
    .delete()
    .eq('document_id', scope.documentId);
  if (verErr) throw new Error(verErr.message);

  await documentsService.deleteDocument(scope.documentId);

  // Project-scoped VD stack only — other workspaces untouched.
  await visualDesignerSurveysService.resetAllForProject(scope.projectId);
}

/**
 * Hard-delete a workspace Invoice with no local residue.
 * Does not touch quotes, estimates, surveys, or other workspaces.
 */
export async function deleteWorkspaceInvoice(params: {
  documentId: string;
  projectId: string;
}): Promise<void> {
  const documentId = String(params.documentId || '').trim();
  const projectId = String(params.projectId || '').trim();
  if (!documentId || !projectId) {
    throw new Error('Missing document or project id');
  }

  const { data: doc, error: docErr } = await directSupabase
    .from('documents')
    .select('id, kind, project_id, lifecycle_ref')
    .eq('id', documentId)
    .maybeSingle();
  if (docErr) throw new Error(docErr.message);
  if (!doc) throw new Error('Invoice not found');
  if (doc.kind !== 'Invoice') {
    throw new Error('Only invoices can be deleted with this action.');
  }
  if (String(doc.project_id || '') !== projectId) {
    throw new Error('This invoice is not linked to this workspace.');
  }

  // job_actuals.invoice_id is ON DELETE RESTRICT — remove before the document.
  const { error: actualsErr } = await directSupabase
    .from('job_actuals')
    .delete()
    .eq('invoice_id', documentId);
  if (actualsErr && !/does not exist|relation .* does not exist/i.test(String(actualsErr.message || ''))) {
    throw new Error(actualsErr.message);
  }

  // Share links / FreeAgent audit / lines cascade (or are cleared inside deleteDocument).
  await documentsService.deleteDocument(documentId);
}

export async function cloneWorkspace(params: {
  source: Project;
  mode: CloneWorkspaceMode;
  projectName?: string | null;
  /** Brand for the copy. Without one it keeps the source's brand while selectable, else the customer's. */
  organisationId?: string | null;
}) {
  if (!params.source.customer_id) {
    throw new Error('Assign a customer before cloning this workspace.');
  }
  const plan = cloneCopyPlan(params.mode);
  const projectDocs = await documentsService.getDocumentsByProject(params.source.id);
  const sourceQuote = pickCloneSourceDocument(projectDocs);
  const sourceKind: CloneSourceKind = sourceQuote?.kind === 'Estimate' ? 'Estimate' : 'Quotation';
  if (plan.copyQuote && !sourceQuote) {
    throw new Error('Create a quote or estimate before copying its details.');
  }

  const name = String(params.projectName || params.source.project_name || '').trim() || params.source.project_name;
  const clonedCustomer = await customerService.getLocalCustomerById(String(params.source.customer_id));
  // The copy takes the chosen brand; otherwise it keeps the source workspace's brand while that can
  // still be chosen, else the customer's. A copied quote follows the new workspace, never the source quote.
  // Cloning is how a workspace changes brand: the copy gets a fresh number from the new brand.
  const brands = await organisationSettings.list();
  const chosenBrand = params.organisationId
    ? brands.find((brand) => brand.id === params.organisationId) || null
    : null;
  if (params.organisationId && !chosenBrand?.isSelectable) {
    throw new Error('That organisation is not available for new workspaces.');
  }
  const sourceBrandId = await getWorkspaceBrandId(params.source.id);
  const sourceBrand = brands.find((brand) => brand.id === sourceBrandId) || null;
  const targetBrand = chosenBrand
    || (sourceBrand?.isSelectable ? sourceBrand : brandForCustomer(brands, clonedCustomer as any));
  if (!targetBrand) throw new Error('No organisation is available for the copied workspace.');
  // Job cards are not copied, so their survey notes land on the copy's first job card.
  const { data: sourceMeta, error: sourceMetaError } = await directSupabase
    .from('projects')
    .select('metadata')
    .eq('id', params.source.id)
    .maybeSingle();
  if (sourceMetaError) throw new Error(sourceMetaError.message);
  const surveyNotes = clonedSurveyNotes(
    { metadata: (sourceMeta?.metadata ?? params.source.metadata) as Project['metadata'] },
    await listProjectJobCards(params.source.id),
  );
  const lifecycleRef = await mintWorkspaceLifecycleRef(targetBrand.id);
  const insertPayload = await withProjectCreator({
      project_name: name,
      customer_id: params.source.customer_id,
      customer_name: params.source.customer_name,
      customer_email: params.source.customer_email || '',
      notes: params.source.notes || '',
      deposit_received: false,
      ordered: false,
      delivery_date: null,
      install_date: null,
      product_types: params.source.product_types || [],
      supplier_quote_received: false,
      supplier_prices: [],
      complete: false,
      workflow_tasks: getFullWorkflowTasksForMode([], !!params.source.supply_only),
      status: 'pending',
      preferred_suppliers: params.source.preferred_suppliers || [],
      onedrive_folder_id: null,
      supply_only: !!params.source.supply_only,
      project_status: 'active',
      lifecycle_ref: lifecycleRef,
      use_billing_details_for_documents: defaultUseBillingDetailsForDocuments(clonedCustomer),
      ...(surveyNotes ? { metadata: { survey_notes: surveyNotes } } : {}),
    });
  const { data: inserted, error } = await directSupabase
    .from('projects')
    .insert([insertPayload])
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  if (!inserted?.id) throw new Error('Failed to create the cloned workspace.');
  await saveWorkspaceBrand(inserted.id, targetBrand.id);
  await copyWorkspaceComments(params.source.id, inserted.id);
  await copyProjectSupplierReferences(params.source.id, inserted.id);
  emitProjectCreatedActivity({
    projectId: inserted.id,
    customerId: params.source.customer_id,
    customerName: params.source.customer_name,
    actorId: (insertPayload as { created_by?: string }).created_by || null,
  });

  if (plan.copyQuote && sourceQuote) {
    const loaded = await documentsService.getActiveVersion(sourceQuote.id);
    if (!loaded) throw new Error('Could not load the source quote to copy.');
    const header = loaded.header || null;
    const lines = excludeVisualDesignerLines(mapVersionLines(loaded.lines || []));
    const created = await documentsService.createDocument({
      kind: sourceKind,
      lifecycleRef,
      organisationId: targetBrand.id,
      isOriginal: true,
      projectId: inserted.id,
      customerId: sourceQuote.customerId,
      customerFirstName: sourceQuote.customerFirstName,
      customerLastName: sourceQuote.customerLastName,
      customerTitle: sourceQuote.customerTitle,
      title: header?.title ?? sourceQuote.title,
      description: header?.description ?? sourceQuote.description ?? '',
      notes: sourceQuote.notes || '',
      status: 'draft',
      currency: sourceQuote.currency || 'GBP',
      hasDeposit: header?.has_deposit != null ? !!header.has_deposit : sourceQuote.hasDeposit,
      depositPercentage: header?.deposit_percentage != null ? Number(header.deposit_percentage) : sourceQuote.depositPercentage,
      depositAmount: header?.deposit_amount != null ? Number(header.deposit_amount) : sourceQuote.depositAmount,
      projectDays: header?.project_days ?? sourceQuote.projectDays ?? null,
      projectHours: header?.project_hours ?? sourceQuote.projectHours ?? null,
      paymentStructureApplicable: header?.payment_structure_applicable != null
        ? !!header.payment_structure_applicable
        : !!sourceQuote.paymentStructureApplicable,
      paymentStructurePattern: header?.payment_structure_pattern != null
        ? String(header.payment_structure_pattern)
        : sourceQuote.paymentStructurePattern,
      source: 'jarvis',
      vatEnabled: header?.vat_enabled != null ? !!header.vat_enabled : sourceQuote.vatEnabled,
      vatPct: header?.vat_pct != null ? Number(header.vat_pct) : sourceQuote.vatPct,
      inputVatSelected: header?.input_vat_selected != null ? !!header.input_vat_selected : sourceQuote.inputVatSelected,
      fees: {
        adminPct: header?.admin_pct != null ? Number(header.admin_pct) : sourceQuote.fees.adminPct,
        adminCap: header?.admin_cap != null ? Number(header.admin_cap) : sourceQuote.fees.adminCap,
        contingencyPct: header?.contingency_pct != null ? Number(header.contingency_pct) : sourceQuote.fees.contingencyPct,
        profitPct: header?.profit_pct != null ? Number(header.profit_pct) : sourceQuote.fees.profitPct,
        developerPct: null,
      },
    }, lines);
    const active = await documentsService.getActiveVersion(created.id);
    const versionId = active?.header?.id ? String(active.header.id) : null;
    await attachFirstSurvey(inserted.id, versionId, plan.copyDesign ? params.source.id : null, created.id);
    return { projectId: String(inserted.id), documentId: created.id, lifecycleRef };
  }

  const created = await createDraftQuote({
    projectId: inserted.id,
    customerId: params.source.customer_id,
    customerName: params.source.customer_name,
    projectName: name,
    lifecycleRef,
    copyFromProjectId: plan.copyDesign ? params.source.id : null,
    kind: sourceKind,
  });
  return { projectId: String(inserted.id), documentId: created.id, lifecycleRef };
}
